import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Scale, ArrowLeft, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { api } from '@/App';
import { toast } from 'sonner';

const CaseDetails = ({ user }) => {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const location = useLocation();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [pollIntervalId, setPollIntervalId] = useState(null);

  useEffect(() => {
    // If case info was passed via navigation state, use it immediately
    if (location.state?.caseInfo) {
      console.log('Using passed case info:', location.state.caseInfo);
      setCaseData({
        case: {
          id: caseId,
          ...location.state.caseInfo
        },
        analysis: null
      });
      setLoading(false);
      setAnalyzing(true);
      toast.info('AI analysis is processing... Results will appear in 3-4 minutes.');
      
      // Start polling after a delay
      setTimeout(() => {
        startPolling();
      }, 3000);
    }
    
    // Always fetch the actual data from backend
    loadCase();
    
    // Cleanup polling on unmount
    return () => {
      if (pollIntervalId) {
        clearInterval(pollIntervalId);
      }
    };
  }, [caseId]);

  const loadCase = async () => {
    try {
      console.log('Loading case from backend:', caseId);
      const response = await api.get(`/cases/${caseId}`);
      console.log('Case loaded from backend:', response.data);
      
      // Update with backend data (includes analysis if available)
      setCaseData(response.data);
      setLoading(false);
      
      // If no analysis exists, start polling (analysis was triggered on backend)
      if (!response.data.analysis && !analyzing) {
        console.log('No analysis found yet, starting polling');
        setAnalyzing(true);
        toast.info('AI analysis is processing... Results will appear in 3-4 minutes.');
        setTimeout(() => {
          startPolling();
        }, 3000);
      } else if (response.data.analysis) {
        console.log('Analysis already exists');
        setAnalyzing(false);
      }
    } catch (error) {
      console.error('Failed to load case:', error);
      // Only show error and redirect if we don't already have case data from state
      if (!caseData) {
        setLoading(false);
        toast.error('Failed to load case details');
        navigate('/dashboard');
      }
    }
  };

  const triggerAnalysis = async () => {
    if (analyzing) {
      console.log('Analysis already in progress, skipping trigger');
      return;
    }
    
    setAnalyzing(true);
    toast.info('Checking analysis status...');
    
    try {
      console.log('Manually triggering analysis check for case:', caseId);
      
      // Check current status first
      const checkResponse = await api.get(`/cases/${caseId}`);
      if (checkResponse.data.analysis) {
        console.log('Analysis found on check, updating state');
        setCaseData(checkResponse.data);
        setAnalyzing(false);
        toast.success('Analysis complete!');
        return;
      }
      
      // Try to trigger analysis (in case background task failed)
      const analyzeResponse = await api.post(`/cases/${caseId}/analyze`);
      console.log('Analysis trigger response:', analyzeResponse.data);
      
      // Start polling
      toast.info('Analysis started. Results will appear in 1-2 minutes.');
      startPolling();
      
    } catch (error) {
      console.error('Analysis trigger error:', error);
      setAnalyzing(false);
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        navigate('/');
      } else if (error.response?.data?.status === 'already_analyzed') {
        console.log('Analysis already completed, reloading case');
        toast.info('Analysis already completed!');
        loadCase();
      } else {
        toast.error('Failed to check analysis. Please refresh the page.');
      }
    }
  };

  const startPolling = () => {
    let pollCount = 0;
    const maxPolls = 72; // 72 * 5 seconds = 6 minutes (to handle 3-4 minute analysis time)
    
    const intervalId = setInterval(async () => {
      pollCount++;
      const elapsed = Math.floor((pollCount * 5) / 60);
      console.log(`Polling attempt ${pollCount}/${maxPolls} (${pollCount * 5}s elapsed) for case ${caseId}`);
      
      try {
        const response = await api.get(`/cases/${caseId}`);
        
        if (response.data.analysis) {
          console.log('Analysis complete! Clearing interval and updating state');
          clearInterval(intervalId);
          setPollIntervalId(null);
          setCaseData(response.data);
          setAnalyzing(false);
          toast.success('Analysis complete!');
        } else if (pollCount >= maxPolls) {
          console.log('Max polling attempts reached, checking one last time...');
          // One final check before giving up
          const finalCheck = await api.get(`/cases/${caseId}`);
          if (finalCheck.data.analysis) {
            console.log('Analysis found on final check!');
            setCaseData(finalCheck.data);
            setAnalyzing(false);
            toast.success('Analysis complete!');
          } else {
            console.log('Analysis still not complete after max polling');
            setAnalyzing(false);
            toast.warning('Analysis is taking longer than expected. The results may be ready now - click "Check Results" below.');
          }
          clearInterval(intervalId);
          setPollIntervalId(null);
        }
      } catch (error) {
        console.error('Polling error:', error);
        
        // On auth error, stop polling
        if (error.response?.status === 401) {
          clearInterval(intervalId);
          setPollIntervalId(null);
          setAnalyzing(false);
          toast.error('Session expired. Please log in again.');
          navigate('/');
        }
        
        // Stop after max attempts even on errors
        if (pollCount >= maxPolls) {
          clearInterval(intervalId);
          setPollIntervalId(null);
          setAnalyzing(false);
          toast.warning('Unable to check status. Click "Check Results" to try again.');
        }
      }
    }, 5000);
    
    setPollIntervalId(intervalId);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getVerdictIcon = (verdict) => {
    if (verdict.includes('Strong Grounds')) return <CheckCircle className="w-6 h-6 text-green-600" />;
    if (verdict.includes('Moderate Grounds')) return <AlertCircle className="w-6 h-6 text-yellow-600" />;
    if (verdict.includes('Weak Grounds') || verdict.includes('No Grounds')) return <XCircle className="w-6 h-6 text-red-600" />;
    return <AlertCircle className="w-6 h-6 text-blue-600" />;
  };

  const getVerdictColor = (verdict) => {
    if (verdict.includes('Strong Grounds')) return 'bg-green-100 text-green-800 border-green-300';
    if (verdict.includes('Moderate Grounds')) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (verdict.includes('Weak Grounds') || verdict.includes('No Grounds')) return 'bg-red-100 text-red-800 border-red-300';
    return 'bg-blue-100 text-blue-800 border-blue-300';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-700 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading case details...</p>
        </div>
      </div>
    );
  }

  const { case: legalCase, analysis } = caseData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Scale className="w-8 h-8 text-blue-700" />
            <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>LegalCheck</span>
          </div>
          
          <div className="flex items-center space-x-4">
            <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full border-2 border-blue-200" />
            <span className="text-slate-700 font-medium hidden sm:block">{user.name}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <Button 
          data-testid="back-button"
          onClick={() => navigate('/dashboard')}
          variant="outline"
          className="mb-8 rounded-full"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Case Header */}
        <Card className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200 mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                  {legalCase.category}
                </span>
                <span className="text-sm text-slate-500">
                  Submitted {formatDate(legalCase.created_at)}
                </span>
              </div>
              <h1 
                data-testid="case-title"
                className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {legalCase.title}
              </h1>
              <p className="text-slate-600 leading-relaxed">
                {legalCase.description}
              </p>
            </div>
          </div>

          {analyzing && (
            <div className="mt-6 p-6 bg-blue-50 border-2 border-blue-300 rounded-xl">
              <div className="flex items-start space-x-3">
                <Loader2 className="w-6 h-6 text-blue-700 animate-spin flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-blue-900 mb-2">⏳ AI Analysis in Progress</h3>
                  <p className="text-blue-800 font-semibold mb-2">
                    Our AI models are analyzing your case. This typically takes 3-4 minutes.
                  </p>
                  <p className="text-blue-700 text-sm mb-3">
                    Results will appear automatically when complete. You can stay on this page or navigate away.
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="h-2 bg-blue-200 rounded-full flex-1 overflow-hidden">
                      <div className="h-full bg-blue-600 rounded-full animate-pulse" style={{width: '60%'}}></div>
                    </div>
                    <span className="text-xs text-blue-600 font-medium">Processing...</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!analyzing && !analysis && caseData && (
            <div className="mt-6 p-6 bg-amber-50 border-2 border-amber-300 rounded-xl">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-amber-900 mb-2">⚠️ Results Not Loaded Yet</h3>
                  <p className="text-amber-800 text-sm mb-2">
                    Your analysis may be complete. Click below to check for results.
                  </p>
                  <p className="text-amber-700 text-xs">
                    Analysis typically takes 3-4 minutes. If you just submitted, please wait a bit longer.
                  </p>
                </div>
                <Button
                  data-testid="check-results-button"
                  onClick={() => {
                    setAnalyzing(true);
                    loadCase();
                  }}
                  className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-6 py-2 rounded-full whitespace-nowrap"
                >
                  Check Results
                </Button>
              </div>
            </div>
          )}

          {analysis && (
            <div className={`mt-6 p-6 border-2 rounded-xl ${getVerdictColor(analysis.consolidated_verdict)}`}>
              <div className="flex items-center gap-3 mb-3">
                {getVerdictIcon(analysis.consolidated_verdict)}
                <h3 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Consolidated AI Verdict
                </h3>
              </div>
              <div className="space-y-3">
                <p className="font-semibold">Confidence Level: {analysis.confidence_level}</p>
                
                {analysis.lawyer_type && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-semibold text-blue-900 mb-1">👔 Recommended Lawyer Type:</p>
                    <p className="text-lg font-bold text-blue-800">{analysis.lawyer_type}</p>
                  </div>
                )}
                
                <div className="mt-4">
                  <p className="font-semibold mb-2">Key Findings:</p>
                  <ul className="list-disc list-inside space-y-1">
                    {analysis.key_findings.map((finding, idx) => (
                      <li key={idx} className="text-sm">{finding}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Analysis Tabs */}
        {analysis ? (
          <Tabs defaultValue="consolidated" className="space-y-8">
            <div className="overflow-x-auto pb-2">
              <TabsList className="bg-white border-2 border-blue-200 p-2 rounded-xl shadow-lg flex w-max min-w-full">
                <TabsTrigger 
                  data-testid="consolidated-tab" 
                  value="consolidated" 
                  className="rounded-lg px-6 py-3 text-base font-bold data-[state=active]:bg-blue-700 data-[state=active]:text-white transition-all duration-200 whitespace-nowrap"
                >
                  Consolidated
                </TabsTrigger>
                <TabsTrigger 
                  data-testid="similar-tab" 
                  value="similar" 
                  className="rounded-lg px-6 py-3 text-base font-bold data-[state=active]:bg-blue-700 data-[state=active]:text-white transition-all duration-200 whitespace-nowrap"
                >
                  Similar Cases
                </TabsTrigger>
                <TabsTrigger 
                  data-testid="openai-tab" 
                  value="openai" 
                  className="rounded-lg px-6 py-3 text-base font-bold data-[state=active]:bg-blue-700 data-[state=active]:text-white transition-all duration-200 whitespace-nowrap"
                >
                  OpenAI
                </TabsTrigger>
                <TabsTrigger 
                  data-testid="claude-tab" 
                  value="claude" 
                  className="rounded-lg px-6 py-3 text-base font-bold data-[state=active]:bg-blue-700 data-[state=active]:text-white transition-all duration-200 whitespace-nowrap"
                >
                  Claude
                </TabsTrigger>
                <TabsTrigger 
                  data-testid="gemini-tab" 
                  value="gemini" 
                  className="rounded-lg px-6 py-3 text-base font-bold data-[state=active]:bg-blue-700 data-[state=active]:text-white transition-all duration-200 whitespace-nowrap"
                >
                  Gemini
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="consolidated">
              <Card className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Consolidated Analysis
                </h2>
                <div data-testid="consolidated-analysis" className="prose max-w-none text-slate-700 whitespace-pre-wrap mb-6">
                  {analysis.consolidated_verdict}
                </div>
                
                {analysis.next_steps && analysis.next_steps.length > 0 && (
                  <div className="mt-8 p-6 bg-blue-50 border-2 border-blue-200 rounded-xl">
                    <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
                      <span>📋</span> Practical Next Steps
                    </h3>
                    <ol className="space-y-3">
                      {analysis.next_steps.map((step, idx) => (
                        <li key={idx} className="flex gap-3">
                          <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                            {idx + 1}
                          </span>
                          <span className="text-slate-700 leading-relaxed pt-1">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="openai">
              <Card className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  OpenAI GPT-5 Analysis
                </h2>
                <div data-testid="openai-analysis" className="prose max-w-none text-slate-700 whitespace-pre-wrap">
                  {analysis.openai_assessment}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="claude">
              <Card className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Claude Sonnet 4 Analysis
                </h2>
                <div data-testid="claude-analysis" className="prose max-w-none text-slate-700 whitespace-pre-wrap">
                  {analysis.claude_assessment}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="gemini">
              <Card className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Gemini 2.5 Pro Analysis
                </h2>
                <div data-testid="gemini-analysis" className="prose max-w-none text-slate-700 whitespace-pre-wrap">
                  {analysis.gemini_assessment}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="similar">
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Similar Legal Cases
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {analysis.similar_cases.map((similarCase, idx) => (
                    <Card 
                      key={idx}
                      data-testid={`similar-case-${idx}`}
                      className="bg-white p-6 rounded-2xl shadow-lg border border-slate-200"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          similarCase.outcome === 'Success' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {similarCase.outcome}
                        </span>
                        <span className="text-xs text-slate-500">{similarCase.year}</span>
                      </div>
                      
                      <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {similarCase.title}
                      </h3>
                      
                      <p className="text-sm text-slate-600 mb-3">
                        {similarCase.description}
                      </p>
                      
                      <div className="pt-3 border-t border-slate-200">
                        <p className="text-xs font-semibold text-slate-700 mb-1">Key Takeaway:</p>
                        <p className="text-xs text-slate-600">{similarCase.takeaway}</p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          <Card className="bg-white p-12 rounded-2xl shadow-xl border border-slate-200 text-center">
            <Loader2 className="w-12 h-12 text-blue-700 animate-spin mx-auto mb-4" />
            <p className="text-slate-600 font-medium">Analysis in progress...</p>
          </Card>
        )}

        {/* Disclaimer */}
        <div className="mt-12 p-6 bg-amber-50 border-2 border-amber-200 rounded-xl">
          <p className="text-sm text-amber-900 font-medium text-center">
            <strong>⚠️ Important Legal Disclaimer:</strong> This AI analysis is for informational purposes only and does not constitute legal advice. 
            The assessments provided should not be used as a substitute for consultation with a licensed attorney. 
            Always seek professional legal counsel before taking any legal action.
          </p>
        </div>
      </main>
    </div>
  );
};

export default CaseDetails;